import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { ResultsView } from "@/components/ResultsView";
import { FieldCompare } from "@/components/FieldCompare";
import { FieldRecheck } from "@/components/FieldRecheck";
import { FieldGallery } from "@/components/FieldGallery";
import { ReportCard } from "@/components/ReportCard";
import { SpeakButton } from "@/components/SpeakButton";
import { SaveOfflineButton } from "@/components/SaveOfflineButton";
import { soilSpeechText } from "@/lib/speech";
import { useAuth } from "@/context/AuthContext";
import apiClient from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Map, Trash2, ChevronDown, Sprout, Plus, FileText, TrendingUp, Scale, Camera, Award } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { downloadReport } from "@/lib/report";
import { toast } from "sonner";

function TrendChart({ history }) {
  const data = (history || []).map((h, i) => ({
    name: `${new Date(h.date).toLocaleDateString(undefined, { month: "short", day: "numeric" })} · #${i + 1}`,
    score: Math.round(h.score || 0),
  }));
  return (
    <div className="bg-[#EAF6EF] rounded-2xl p-5 mb-6" data-testid="trend-chart">
      <h4 className="font-display text-sm font-semibold text-emerald-900 mb-3 flex items-center gap-2">
        <TrendingUp className="h-4 w-4" /> Soil health over time
      </h4>
      <ResponsiveContainer width="100%" height={180}>
        <LineChart data={data} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#cde6d8" />
          <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#57534e" }} />
          <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#57534e" }} />
          <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #52B788", fontSize: 13 }} />
          <Line type="monotone" dataKey="score" stroke="#2D6A4F" strokeWidth={3} dot={{ r: 4, fill: "#2D6A4F" }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function ComparePanel({ fields }) {
  const [a, setA] = useState(fields[0]?.id);
  const [b, setB] = useState(fields[1]?.id);
  const byId = (id) => fields.find((f) => f.id === id);
  const Picker = ({ value, onChange, testid }) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="bg-white"><SelectValue placeholder="Choose a field" /></SelectTrigger>
      <SelectContent>{fields.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
    </Select>
  );
  return (
    <div className="mb-8 space-y-4" data-testid="compare-panel">
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
        <Picker value={a} onChange={setA} testid="compare-a-select" />
        <span className="text-xs uppercase tracking-wider text-stone-500 font-semibold">vs</span>
        <Picker value={b} onChange={setB} testid="compare-b-select" />
      </div>
      {a === b ? <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="compare-same-warning">Pick two different fields to compare.</p>
        : <FieldCompare a={byId(a)} b={byId(b)} />}
    </div>
  );
}

export default function Fields() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [fields, setFields] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [compare, setCompare] = useState(false);
  const [recheck, setRecheck] = useState(null);
  const [reportFor, setReportFor] = useState(null);

  const replaceField = (updated) => setFields((fs) => fs.map((f) => (f.id === updated.id ? updated : f)));

  const load = () => apiClient.get("/fields").then((r) => setFields(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const del = async (id, e) => {
    e.stopPropagation();
    try {
      await apiClient.delete(`/fields/${id}`);
      setFields((f) => f.filter((x) => x.id !== id));
      toast.success("Field deleted");
    } catch { toast.error("Could not delete"); }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-emerald-950 tracking-tight">Saved Fields</h1>
            <p className="text-stone-600 mt-1">Your soil records and past analyses.</p>
          </div>
          <div className="flex items-center gap-2">
            {fields.length >= 2 && (
              <Button data-testid="compare-toggle-btn" onClick={() => setCompare((c) => !c)} variant={compare ? "default" : "outline"}
                className={`rounded-full px-5 ${compare ? "bg-[#D97706] hover:bg-[#78350F] text-white" : "border-stone-300"}`}>
                <Scale className="h-4 w-4 mr-1" /> Compare
              </Button>
            )}
            <Button data-testid="new-analysis-btn" onClick={() => navigate("/app/analyze")} className="bg-[#2D6A4F] hover:bg-[#143D29] text-white rounded-full px-5">
              <Plus className="h-4 w-4 mr-1" /> New
            </Button>
          </div>
        </div>

        {compare && fields.length >= 2 && <ComparePanel fields={fields} />}

        {loading ? (
          <p className="text-stone-500">Loading…</p>
        ) : fields.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-stone-300 p-12 text-center" data-testid="fields-empty">
            <div className="h-14 w-14 rounded-2xl bg-[#EAF6EF] flex items-center justify-center mx-auto mb-4">
              <Map className="h-7 w-7 text-[#2D6A4F]" />
            </div>
            <h3 className="font-display text-xl font-semibold text-emerald-950 mb-2">No fields yet</h3>
            <p className="text-stone-600 mb-6">Run a soil analysis and save it to build your field library.</p>
            <Button onClick={() => navigate("/app/analyze")} className="bg-[#D97706] hover:bg-[#78350F] text-white rounded-full px-6">Analyse soil</Button>
          </div>
        ) : (
          <div className="space-y-4">
            {fields.map((f) => (
              <div key={f.id} data-testid={`field-${f.id}`} className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
                <button onClick={() => setOpenId(openId === f.id ? null : f.id)} className="w-full flex items-center justify-between gap-4 p-5 text-left">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-11 w-11 rounded-xl bg-[#EAF6EF] flex items-center justify-center shrink-0">
                      <Sprout className="h-5 w-5 text-[#2D6A4F]" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-display font-semibold text-emerald-950 truncate">{f.name}</div>
                      <div className="text-sm text-stone-500 font-mono-tech truncate">
                        {f.location || "—"} · pH {f.soil?.ph} · {f.soil?.soil_type} · Score {Math.round(f.analysis?.soil_health?.score || 0)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span onClick={(e) => del(f.id, e)} data-testid={`delete-field-${f.id}`} className="p-2 rounded-lg hover:bg-red-50 text-red-500 cursor-pointer">
                      <Trash2 className="h-4 w-4" />
                    </span>
                    <ChevronDown className={`h-5 w-5 text-stone-400 transition-transform ${openId === f.id ? "rotate-180" : ""}`} />
                  </div>
                </button>
                {openId === f.id && (
                  <div className="p-5 pt-0 border-t border-stone-100">
                    <div className="pt-6">
                      {f.history?.length > 1 && <TrendChart history={f.history} />}
                      <div className="flex flex-wrap justify-end gap-2 mb-4">
                        <SpeakButton text={soilSpeechText(f.analysis, f.name)} language={user?.language} prefetch />
                        <SaveOfflineButton item={{ id: `field-${f.id}`, kind: "soil", title: f.name, subtitle: `Score ${Math.round(f.analysis?.soil_health?.score || 0)} · ${f.soil?.soil_type || ""}${f.location ? ` · ${f.location}` : ""}`, payload: { location: f.location, soil: f.soil, analysis: f.analysis } }} />
                        <Button data-testid={`report-card-${f.id}`} onClick={() => setReportFor(f)} variant="outline" className="border-[#D97706] text-[#78350F] hover:bg-amber-50">
                          <Award className="h-4 w-4 mr-2" /> Season report card
                        </Button>
                        <Button data-testid={`recheck-field-${f.id}`} onClick={() => setRecheck(f)} className="bg-[#D97706] hover:bg-[#78350F] text-white">
                          <Camera className="h-4 w-4 mr-2" /> Recheck with photo
                        </Button>
                        <Button data-testid={`download-pdf-${f.id}`} onClick={() => downloadReport({ name: f.name, location: f.location, soil: f.soil, analysis: f.analysis })} variant="outline" className="border-stone-300">
                          <FileText className="h-4 w-4 mr-2" /> Download PDF
                        </Button>
                      </div>
                      <FieldGallery field={f} />
                      <ResultsView analysis={f.analysis} />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
      {recheck && <FieldRecheck field={recheck} open={!!recheck} onOpenChange={(o) => !o && setRecheck(null)} onDone={replaceField} />}
      {reportFor && <ReportCard field={reportFor} open={!!reportFor} onOpenChange={(o) => !o && setReportFor(null)} />}
    </div>
  );
}
